import { useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import type { LeadCollectionRecord, LeadImportPreviewItemRecord, LeadImportPreviewRecord } from '@shared/types';
import { Icon } from '@renderer/shared/components/icons';
import { useI18n } from '@renderer/shared/i18n';
import { exportLeadsJson, importLeadsJson, readTextFromClipboard, saveLeadsJsonFile } from './leads-repository';

function getPreviewActionTone(action: LeadImportPreviewItemRecord['action']): string {
  switch (action) {
    case 'create':
      return 'success';
    case 'skip_existing':
      return 'warning';
    case 'duplicate_in_file':
      return 'info';
    default:
      return 'neutral';
  }
}

async function readFileText(file: File): Promise<string> {
  return file.text();
}

export function LeadImportExportPanel({
  onCollectionChange
}: {
  onCollectionChange: (collection: LeadCollectionRecord) => void;
}): JSX.Element {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [jsonText, setJsonText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState<LeadImportPreviewRecord | null>(null);
  const [importedCount, setImportedCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [busyAction, setBusyAction] = useState<'import' | 'export' | null>(null);
  const previewItems = useMemo(() => preview?.items.slice(0, 8) ?? [], [preview]);
  const warningCount = useMemo(() => preview?.items.reduce((total, item) => total + item.warnings.length, 0) ?? 0, [preview]);

  function resetImportFeedback(): void {
    setPreview(null);
    setImportedCount(null);
    setStatusMessage(null);
    setError(null);
  }

  async function loadJsonTextFromFile(file: File): Promise<void> {
    const text = await readFileText(file);
    setJsonText(text);
    setFileName(file.name);
    resetImportFeedback();
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';

    if (!file) {
      return;
    }

    try {
      await loadJsonTextFromFile(file);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('leads.importExport.errors.fileReadFailed'));
    }
  }

  async function handleDrop(event: DragEvent<HTMLDivElement>): Promise<void> {
    event.preventDefault();
    setDragActive(false);

    const file = event.dataTransfer.files?.[0] ?? null;
    if (file) {
      try {
        await loadJsonTextFromFile(file);
      } catch (nextError) {
        setError(nextError instanceof Error ? nextError.message : t('leads.importExport.errors.fileReadFailed'));
      }
      return;
    }

    const text = event.dataTransfer.getData('text/plain');
    if (text.trim()) {
      setJsonText(text);
      setFileName(null);
      resetImportFeedback();
    }
  }

  async function getImportJson(): Promise<string> {
    const typedJson = jsonText.trim();

    if (typedJson) {
      return jsonText;
    }

    const clipboardText = await readTextFromClipboard();
    if (clipboardText.trim()) {
      setJsonText(clipboardText);
      setFileName(null);
      return clipboardText;
    }

    throw new Error(t('leads.importExport.errors.emptyJson'));
  }

  async function handleImport(): Promise<void> {
    setBusyAction('import');
    setError(null);
    setStatusMessage(null);

    try {
      const importJson = await getImportJson();
      const result = await importLeadsJson({ json: importJson });
      setPreview(result.preview);
      setImportedCount(result.importedLeadIds.length);
      onCollectionChange(result.collection);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('leads.importExport.errors.importFailed'));
    } finally {
      setBusyAction(null);
    }
  }

  async function handleExportSave(): Promise<void> {
    setBusyAction('export');
    setError(null);
    setStatusMessage(null);

    try {
      const content = await exportLeadsJson();
      const result = await saveLeadsJsonFile(content, t('leads.importExport.export.dialogTitle'));
      if (result.saved) {
        setStatusMessage(t('leads.importExport.messages.exportSaved'));
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('leads.importExport.errors.exportFailed'));
    } finally {
      setBusyAction(null);
    }
  }

  return (
    <div className="av-lead-import-export">
      <section className="av-lead-import-export__section">
        <div className="av-lead-import-export__header">
          <div>
            <h3>{t('leads.importExport.import.title')}</h3>
            <p>{t('leads.importExport.import.description')}</p>
          </div>
          <button className="av-primary-button" disabled={busyAction === 'import'} type="button" onClick={() => void handleImport()}>
            {busyAction === 'import' ? t('leads.importExport.actions.importing') : t('leads.importExport.actions.import')}
          </button>
        </div>

        <input ref={fileInputRef} accept=".json,application/json" hidden type="file" onChange={(event) => void handleFileChange(event)} />

        <div
          className={['av-lead-import-export__dropzone', dragActive ? 'is-active' : ''].filter(Boolean).join(' ')}
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={(event) => {
            event.preventDefault();
            setDragActive(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(event) => void handleDrop(event)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              fileInputRef.current?.click();
            }
          }}
        >
          <Icon name="document" />
          <div>
            <strong>{fileName ? t('leads.importExport.import.fileReady', { file: fileName }) : t('leads.importExport.import.dropTitle')}</strong>
            <span>{t('leads.importExport.import.dropSubtitle')}</span>
          </div>
        </div>

        <label className="av-form-field">
          <span>{t('leads.importExport.import.pasteLabel')}</span>
          <textarea
            className="av-input av-lead-import-export__textarea"
            placeholder={t('leads.importExport.import.placeholder')}
            value={jsonText}
            onChange={(event) => {
              setJsonText(event.target.value);
              setFileName(null);
              resetImportFeedback();
            }}
          />
        </label>

        {preview ? (
          <div className="av-lead-import-export__preview">
            <div className="av-lead-import-export__summary">
              <span>
                <strong>{preview.importableCount}</strong>
                {t('leads.importExport.summary.new')}
              </span>
              <span>
                <strong>{preview.skippedExistingCount}</strong>
                {t('leads.importExport.summary.existing')}
              </span>
              <span>
                <strong>{preview.duplicateInFileCount}</strong>
                {t('leads.importExport.summary.duplicates')}
              </span>
              <span>
                <strong>{preview.invalidCount}</strong>
                {t('leads.importExport.summary.invalid')}
              </span>
              <span>
                <strong>{warningCount}</strong>
                {t('leads.importExport.summary.warnings')}
              </span>
            </div>
            <div className="av-lead-import-export__rows">
              {previewItems.map((item) => (
                <div key={`${item.rowIndex}-${item.action}`} className="av-lead-import-export__row">
                  <span>{t('leads.importExport.preview.row', { row: item.rowIndex })}</span>
                  <strong>{item.name ?? t('common.notSet')}</strong>
                  <em className={`is-${getPreviewActionTone(item.action)}`}>{t(`leads.importExport.actions.${item.action}`)}</em>
                  <small>
                    {item.matchLeadName
                      ? t('leads.importExport.preview.match', { name: item.matchLeadName })
                      : item.reason
                        ? t(`leads.importExport.reasons.${item.reason}`)
                        : item.warnings.length > 0
                          ? t('leads.importExport.preview.warningCount', { count: item.warnings.length })
                          : t('leads.importExport.preview.ready')}
                  </small>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {importedCount !== null ? <div className="av-inline-success">{t('leads.importExport.messages.imported', { count: importedCount })}</div> : null}
      </section>

      <details className="av-lead-import-export__export">
        <summary>{t('leads.importExport.export.title')}</summary>
        <div className="av-lead-import-export__export-body">
          <p>{t('leads.importExport.export.description')}</p>
          <button className="av-secondary-button" disabled={busyAction === 'export'} type="button" onClick={() => void handleExportSave()}>
            {busyAction === 'export' ? t('leads.importExport.actions.exporting') : t('leads.importExport.actions.export')}
          </button>
        </div>
      </details>

      {error ? <div className="av-inline-error">{error}</div> : null}
      {statusMessage ? <div className="av-inline-success">{statusMessage}</div> : null}
    </div>
  );
}
